import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Linkedin, Facebook, Instagram, Hash } from 'lucide-react';

export const ConnectPortal: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState<string | null>(null);

  const [connectedPlatforms, setConnectedPlatforms] = useState<string[]>([]);

  useEffect(() => {
    const fetchLink = async () => {
      try {
        const response = await fetch(`/api/connect/${token}`);
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to load connection link');
        }
        const linkData = await response.json();
        setData(linkData);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchLink();
  }, [token]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'OAUTH_AUTH_SUCCESS' && event.data?.platform && event.data?.account) {
        try {
          const response = await fetch(`/api/connect/${token}/save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ account: event.data.account })
          });
          
          if (!response.ok) {
            throw new Error('Failed to save connection');
          }
          
          setConnectedPlatforms(prev => [...prev, event.data.platform]);
        } catch (err) {
          console.error('Failed to save account:', err);
          alert('Failed to save the connection. Please try again.');
        } finally {
          setIsConnecting(null);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [token]);

  const handleConnectSocial = async (platform: string, isPages: boolean = false) => {
    setIsConnecting(platform);
    try {
      const p = platform.toLowerCase();
      const type = isPages && p === 'linkedin' ? 'linkedin/pages' : p;
      const response = await fetch(`/api/auth/${type}/url?brandId=${data.brandId}&clientFlow=true`);
      if (!response.ok) throw new Error('Failed to get auth URL');
      const { url } = await response.json();

      const authWindow = window.open(url, 'oauth_popup', 'width=600,height=700');
      if (!authWindow) {
        alert("Please allow popups to connect your account.");
        setIsConnecting(null);
      } else {
        const checkClosed = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkClosed);
            setIsConnecting(prev => prev === platform ? null : prev);
          }
        }, 1000);
      }
    } catch (err) {
      console.error("OAuth error:", err);
      alert("Failed to initiate connection.");
      setIsConnecting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-gray-900">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-4" />
        <p className="text-gray-500 font-medium">Loading portal...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center text-gray-900">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 max-w-md w-full">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-500 mb-6">{error || "This connection link is invalid or has expired."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-gray-900">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-indigo-600 p-8 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white opacity-10 rounded-full blur-3xl"></div>
          {data.brandLogo ? (
            <img src={data.brandLogo} alt={data.brandName} className="w-20 h-20 rounded-2xl object-cover mx-auto mb-4 shadow-lg ring-4 ring-white/20" />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-white/20 mx-auto mb-4 flex items-center justify-center text-white text-3xl font-bold shadow-lg ring-4 ring-white/20">
              {(data.brandName || "Brand").substring(0, 2).toUpperCase()}
            </div>
          )}
          <h1 className="text-2xl font-bold text-white mb-1">Connect Your Accounts</h1>
          <p className="text-indigo-100 text-sm">Link your social media profiles to <strong>{data.brandName || "your brand"}</strong>.</p>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          <p className="text-gray-600 text-sm text-center mb-6">
            Connecting your accounts allows the platform to securely publish content on your behalf. You can revoke access at any time.
          </p>

          <div className="space-y-4">
            {/* LinkedIn */}
            {data.platforms?.includes('LinkedIn') && (
              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100/80 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#0077b5] text-white flex items-center justify-center shadow-sm">
                    <Linkedin className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm">LinkedIn</h3>
                    <p className="text-xs text-gray-500">Connect Page or Profile</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleConnectSocial('LinkedIn', true)}
                  disabled={isConnecting === 'LinkedIn' || connectedPlatforms.includes('LinkedIn')}
                  className={`px-4 py-2 bg-white border border-gray-200 shadow-sm text-xs font-bold rounded-lg transition-colors ${connectedPlatforms.includes('LinkedIn') ? 'text-green-600 border-green-200 bg-green-50' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  {isConnecting === 'LinkedIn' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : connectedPlatforms.includes('LinkedIn') ? 'Connected' : 'Connect'}
                </button>
              </div>
            )}

            {/* Facebook */}
            {data.platforms?.includes('Facebook') && (
              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100/80 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#1877F2] text-white flex items-center justify-center shadow-sm">
                    <Facebook className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm">Facebook</h3>
                    <p className="text-xs text-gray-500">Connect Page or Group</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleConnectSocial('Facebook')}
                  disabled={isConnecting === 'Facebook' || connectedPlatforms.includes('Facebook')}
                  className={`px-4 py-2 bg-white border border-gray-200 shadow-sm text-xs font-bold rounded-lg transition-colors ${connectedPlatforms.includes('Facebook') ? 'text-green-600 border-green-200 bg-green-50' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  {isConnecting === 'Facebook' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : connectedPlatforms.includes('Facebook') ? 'Connected' : 'Connect'}
                </button>
              </div>
            )}

            {/* Instagram */}
            {data.platforms?.includes('Instagram') && (
              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100/80 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-tr from-[#fd5949] to-[#d6249f] text-white flex items-center justify-center shadow-sm">
                    <Instagram className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm">Instagram</h3>
                    <p className="text-xs text-gray-500">Connect Business Account</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleConnectSocial('Instagram')}
                  disabled={isConnecting === 'Instagram' || connectedPlatforms.includes('Instagram')}
                  className={`px-4 py-2 bg-white border border-gray-200 shadow-sm text-xs font-bold rounded-lg transition-colors ${connectedPlatforms.includes('Instagram') ? 'text-green-600 border-green-200 bg-green-50' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  {isConnecting === 'Instagram' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : connectedPlatforms.includes('Instagram') ? 'Connected' : 'Connect'}
                </button>
              </div>
            )}
            
            {/* Threads */}
            {data.platforms?.includes('Threads') && (
              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100/80 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-black text-white flex items-center justify-center shadow-sm">
                    <Hash className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm">Threads</h3>
                    <p className="text-xs text-gray-500">Connect Profile</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleConnectSocial('Threads')}
                  disabled={isConnecting === 'Threads' || connectedPlatforms.includes('Threads')}
                  className={`px-4 py-2 bg-white border border-gray-200 shadow-sm text-xs font-bold rounded-lg transition-colors ${connectedPlatforms.includes('Threads') ? 'text-green-600 border-green-200 bg-green-50' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  {isConnecting === 'Threads' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : connectedPlatforms.includes('Threads') ? 'Connected' : 'Connect'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
